import { useState } from 'react';
import { useNavigate } from 'react-router';

import { useUsuario } from '@/lib/auth/contexto';
import { empresasCom } from '@/lib/auth/permissoes';
import { mensagemDeErro } from '@/lib/erros';
import { formatarDocumento } from '@/lib/formato';
import { Dropzone } from '@/ui/components/dropzone';
import { SelectPesquisavel } from '@/ui/components/select-pesquisavel';
import { toast } from '@/ui/primitives/aviso';
import { Button } from '@/ui/primitives/button';
import { Campo } from '@/ui/primitives/input';

import { useEnviarLote } from './api';
import { detectar, EXTENSOES } from './deteccao';
import { juntarSemRepetir } from './selecao';
import { SelecaoArquivos } from './selecao-arquivos';

/** O relatório da SEFAZ não diz a destinatária: a empresa é escolhida (lista que cresce → busca). */
function CampoEmpresaDestinataria(p: {
  empresas: readonly { empresa: { id: string; cnpj: string; razaoSocial: string } }[];
  valor: string | null;
  aoMudar: (id: string | null) => void;
}) {
  return (
    <Campo
      rotulo="Empresa destinatária"
      ajuda="O relatório da SEFAZ não diz de qual empresa é: escolha a que consultou."
    >
      {(a11y) => (
        <SelectPesquisavel
          {...a11y}
          rotuloBusca="Buscar empresa"
          opcoes={p.empresas.map((v) => ({
            valor: v.empresa.id,
            rotulo: v.empresa.razaoSocial,
            detalhe: formatarDocumento(v.empresa.cnpj),
          }))}
          valor={p.valor}
          aoMudar={p.aoMudar}
          placeholder="Escolha a empresa"
          vazio="Nenhuma empresa encontrada."
        />
      )}
    </Campo>
  );
}

/** Texto da área de soltar: convite quando vazia; "acrescentar" quando já há arquivos. */
const AREA = {
  vazia: {
    titulo: 'Arraste os arquivos ou clique para escolher',
    descricao: 'XMLs soltos ou em ZIP, ou a planilha de notas destinadas da SEFAZ (CSV/XLSX).',
  },
  acrescentar: {
    titulo: 'Adicionar mais arquivos',
    descricao: 'Arraste aqui ou clique. Arquivo repetido não entra duas vezes.',
  },
} as const;

/** Escolher arquivos → conferir → enviar. O tipo (documentos ou planilha) é detectado sozinho. */
export function FormularioEnvio() {
  const usuario = useUsuario();
  const navegar = useNavigate();
  const enviar = useEnviarLote();
  const [arquivos, setArquivos] = useState<File[]>([]);
  const [empresaId, setEmpresaId] = useState<string>();
  const deteccao = detectar(arquivos);
  const ehPlanilha = deteccao.ok && deteccao.tipo === 'PLANILHA';
  const empresas = empresasCom(usuario, ehPlanilha ? 'chaves.gerenciar' : 'documentos.importar');
  const faltaEmpresa = ehPlanilha && !empresaId;

  const confirmar = () => {
    if (!deteccao.ok || faltaEmpresa) return;
    enviar.mutate(
      { tipo: deteccao.tipo, arquivos: deteccao.arquivos, empresaId },
      {
        onSuccess: (lote) => {
          setArquivos([]);
          toast.success('Lote recebido', {
            description: 'O processamento começou.',
            action: { label: 'Acompanhar', onClick: () => void navegar(`/importacao/${lote.id}`) },
          });
          void navegar(`/importacao/${lote.id}`);
        },
        onError: (erro) => toast.error(mensagemDeErro(erro)),
      },
    );
  };

  const vazio = arquivos.length === 0;
  return (
    <section className="grid gap-4" aria-label="Novo envio">
      <Dropzone
        aceita={EXTENSOES}
        compacto={!vazio}
        aoSoltar={(novos) => {
          setArquivos((atuais) => juntarSemRepetir(atuais, novos));
        }}
        {...(vazio ? AREA.vazia : AREA.acrescentar)}
      />
      {!vazio && (
        <div className="grid gap-4 rounded-painel border border-borda bg-superficie p-4 animate-surgir">
          <SelecaoArquivos
            arquivos={arquivos}
            aoTirar={(arquivo) => {
              setArquivos((a) => a.filter((x) => x !== arquivo));
            }}
          />
          {!deteccao.ok && (
            <p role="alert" className="text-sm text-perigo">
              {deteccao.motivo}
            </p>
          )}
          {ehPlanilha && (
            <CampoEmpresaDestinataria
              empresas={empresas}
              valor={empresaId ?? null}
              aoMudar={(id) => {
                setEmpresaId(id ?? undefined);
              }}
            />
          )}
          <RodapeEnvio
            planilha={ehPlanilha}
            quantidade={arquivos.length}
            enviando={enviar.isPending}
            pode={deteccao.ok && !faltaEmpresa}
            aoLimpar={() => {
              setArquivos([]);
            }}
            aoImportar={confirmar}
          />
        </div>
      )}
    </section>
  );
}

function RodapeEnvio(p: {
  planilha: boolean;
  quantidade: number;
  enviando: boolean;
  pode: boolean;
  aoLimpar: () => void;
  aoImportar: () => void;
}) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 border-t border-borda pt-4">
      <span className="text-sm text-texto-suave">
        {p.planilha
          ? 'Cada chave do relatório vira uma nota aguardando o XML.'
          : 'Notas que já estão no sistema são reconhecidas pela chave.'}
      </span>
      <div className="flex gap-2">
        <Button variante="fantasma" onClick={p.aoLimpar}>
          Limpar
        </Button>
        <Button
          variante="primario"
          onClick={p.aoImportar}
          carregando={p.enviando}
          disabled={!p.pode}
        >
          {rotuloImportar(p.quantidade, p.planilha)}
        </Button>
      </div>
    </div>
  );
}

function rotuloImportar(quantidade: number, planilha: boolean): string {
  if (planilha) return 'Importar relatório';
  return quantidade === 1 ? 'Importar 1 arquivo' : `Importar ${String(quantidade)} arquivos`;
}
